import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Loader2 } from "lucide-react";

interface ProductService {
  id: string;
  name: string;
  description: string | null;
  price: number | null;
  photos: any[] | null;
}

interface LandingProductsServicesSectionProps {
  workspaceId: string;
  theme?: any;
  content?: any;
  isStandalonePage?: boolean;
  ssrProductsData?: any[] | null;
  onTrackProductView?: (productId: string, productName: string) => void;
}

const LandingProductsServicesSection = ({
  workspaceId,
  theme,
  content,
  isStandalonePage = false,
  ssrProductsData,
  onTrackProductView,
}: LandingProductsServicesSectionProps) => {
  const { subdomain } = useParams<{ subdomain: string }>();
  const navigate = useNavigate();
  const [products, setProducts] = useState<ProductService[]>(ssrProductsData || []);
  const [loading, setLoading] = useState(!ssrProductsData);

  // Check if we're on an actual subdomain (subdomain.localhost) vs testing route (/subdomain/:subdomain)
  const isOnSubdomain = () => {
    if (typeof window !== 'undefined') {
      const hostname = window.location.hostname;
      const parts = hostname.split('.');
      // subdomain.localhost or subdomain.my-domain.com
      return parts.length >= 2 && parts[0] !== 'localhost' && parts[0] !== 'www';
    }
    return false;
  };

  const getBasePath = () => {
    return isOnSubdomain() ? '' : `/subdomain/${subdomain}`;
  };

  useEffect(() => {
    // If we have SSR data, don't fetch again
    if (ssrProductsData) {
      return;
    }

    fetchProducts();
  }, [workspaceId, isStandalonePage, ssrProductsData]);

  const fetchProducts = async () => {
    try {
      setLoading(true);
      const limit = isStandalonePage ? 12 : 6;

      const { data, error } = await supabase
        .from("products_services")
        .select("id, name, description, price, photos")
        .eq("workspace_id", workspaceId)
        .order("created_at", { ascending: false })
        .limit(limit);

      if (error) {
        console.error("Error fetching products/services:", error);
        return;
      }

      setProducts(data || []);
    } catch (err) {
      console.error("Error fetching products/services:", err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <section className="py-16 lg:py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-center">
            <Loader2 className="h-8 w-8 animate-spin text-gray-600" />
          </div>
        </div>
      </section>
    );
  }

  if (!products || products.length === 0) {
    return null;
  }

  return (
    <section id="products-services" className="py-16 lg:py-24 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2
            className="text-3xl md:text-4xl font-bold mb-4"
            style={{ color: "var(--primary)" }}
          >
            {content?.heading || content?.title || "Our Products & Services"}
          </h2>
          <div className="w-24 h-1 mx-auto" style={{ backgroundColor: "var(--primary)" }} />
          <p className="mt-6 text-lg text-gray-600 max-w-2xl mx-auto">
            {content?.description || content?.subtitle || "Explore our range of quality products and professional services"}
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {products.map((product) => {
            console.log("Product photos data:", product.photos);
            const photosArray = Array.isArray(product.photos) ? product.photos : [];
            const firstPhoto = photosArray[0];
            console.log("First photo:", firstPhoto);
            // Photos can be either strings (URLs) or objects with url properties
            const photoUrl = typeof firstPhoto === 'string' ? firstPhoto : (firstPhoto?.sourceUrl || firstPhoto?.url || firstPhoto?.googleUrl || firstPhoto?.thumbnailUrl);
            console.log("Photo URL:", photoUrl);

            return (
              <Card
                key={product.id}
                className="product-card overflow-hidden hover:shadow-lg transition-shadow cursor-pointer"
                onClick={() => {
                  if (onTrackProductView) {
                    onTrackProductView(product.id, product.name);
                  }
                  navigate(`${getBasePath()}/product/${product.id}`);
                }}
              >
                {photoUrl && (
                  <div className="aspect-video w-full overflow-hidden bg-gray-200">
                    <img
                      src={photoUrl}
                      alt={product.name}
                      className="w-full h-full object-cover"
                    />
                  </div>
                )}
                <CardContent className="p-6">
                  <h3 className="text-xl font-semibold mb-2 text-gray-900">
                    {product.name}
                  </h3>
                  {product.description && (
                    <p className="text-gray-600 mb-4 line-clamp-3">
                      {product.description}
                    </p>
                  )}
                  {product.price && (
                    <div className="flex items-center gap-1">
                      <span
                        className="text-lg font-bold"
                        style={{ color: "var(--primary)" }}
                      >
                        ₹{product.price.toFixed(2)}
                      </span>
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>

        {!isStandalonePage && (
          <div className="text-center mt-12">
            <Button
              onClick={() => navigate(`${getBasePath()}/products-services`)}
              style={{ backgroundColor: "var(--primary)", color: "var(--primary-foreground)" }}
              className="hover:opacity-90"
            >
              Show More Products & Services
            </Button>
          </div>
        )}
      </div>
    </section>
  );
};

export default LandingProductsServicesSection;